import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Grid from 'UIKit/Box/Grid'
import { BoxProps } from 'UIKit/Box/types'
import Popper from 'UIKit/Popper'
import { PopperContentProps } from 'UIKit/Popper/typpes'
import Text from 'UIKit/Text'
import { LANGUAGES } from 'config/constants/languages'
import useToggle from 'hooks/useToggle'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getIcon, Icons } from 'svgs'
import { colors } from 'theme/colors'
import { setCookie } from 'utils/cookie'
import { useRouter } from 'next/router'
import { RowCenter } from '../Row'

const StyledLanguagePopupContainer = styled(Grid)`
  background-color: ${({ theme }) => theme.colors.inputBlur};
  border-radius: ${({ theme }) => theme.radii.small};
  backdrop-filter: blur(15px);
  padding: 12px 6px;
`

const StyledLanguageItem = styled(Flex)`
  align-items: center;
  border-radius: ${({ theme }) => theme.radii.small};
  padding: 6px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);

    ${Text} {
      color: ${({ theme }) => theme.colors.text};
    }
  }
`

const StyledLangugeSelected = styled(Flex)<{ $Small?: boolean }>`
  width: 68px;
  height: 36px;
  backdrop-filter: blur(2px);
  padding: 10px 8px;
  border-radius: ${({ theme: { radii } }) => radii.tiny};
  border: ${({ theme: { colors } }) => `1px solid ${colors.buttonBackgroundAlt2}`};
`

const LanguageSelect: React.FC<{ small?: boolean } & BoxProps> = ({ small, ...props }) => {
  const [toggle, setToggle] = useToggle(false)
  const { i18n } = useTranslation()

  const selectedLanguage = useMemo(
    () => LANGUAGES.find((item) => item.code.toLowerCase() === i18n.language?.toLowerCase()),
    [i18n.language],
  )
  const Logo = getIcon(selectedLanguage?.logo)

  return (
    <Box {...props}>
      <Popper
        style={{
          width: 'max-content',
        }}
        open={toggle}
        setOpen={setToggle}
        dropdownContent={<LanguagePopup />}
        dropdownPosition="bottom-right"
        id="language-selected"
        className="language-selector"
      >
        <StyledLangugeSelected $Small={small} alignItems="center">
          <Logo style={{ borderRadius: '50%' }} />
          <Box width="8px" />
          {!small && <Icons.ArrowDownIcon fill={colors.textSubtle} />}
        </StyledLangugeSelected>
      </Popper>
    </Box>
  )
}

const LanguagePopup: React.FC<PopperContentProps> = ({ onDismiss }) => {
  const router = useRouter()

  const handleSelect = (code: string) => {
    setCookie('APP_LANG', code)
    const { pathname, query } = router
    router.push({ pathname, query }, '', { locale: 'en', unstable_skipClientCache: true })

    if (onDismiss) onDismiss()
  }

  return (
    <StyledLanguagePopupContainer gridTemplateColumns="1fr 1fr" background={colors.background} mt="12px">
      {LANGUAGES.map((item) => (
        <LanguageItem key={item.code} item={item} handleSelect={handleSelect} />
      ))}
    </StyledLanguagePopupContainer>
  )
}

const LanguageItem: React.FC<{ item: any; handleSelect: (code: string) => void }> = ({ item, handleSelect }) => {
  const Logo = getIcon(item.logo)
  return (
    <StyledLanguageItem
      id={`language-select-item-${item.code}`}
      onClick={() => {
        handleSelect(item.code)
      }}
    >
      <RowCenter width="24px" height="24px">
        <Logo style={{ borderRadius: '50%' }} />
      </RowCenter>
      <Text bold ml="12px" color="textSubtle" fontSize="14px">
        {item.name}
      </Text>
    </StyledLanguageItem>
  )
}

export default LanguageSelect
